import { Button, Illustration, Typography } from "@saviynt/design-system";
import { useNavigate } from "@tanstack/react-router";

interface ErrorPageProps {
  /** The illustration variant to display */
  illustrationKind: "Error" | "Warning";
  /** HTTP status code (e.g. "404", "403") shown as large visual text */
  statusCode: string;
  /** Primary heading describing the error */
  title: string;
  /** Descriptive text explaining what happened */
  description: string;
  /** Label for the back-to-home button */
  buttonLabel: string;
  /** Test ID for the wrapper element */
  testId: string;
}

/**
 * Shared error page layout used by PageNotFound (404) and AccessDenied (403).
 * Provides a consistent visual structure and correct heading hierarchy (h1 for title).
 */
function ErrorPage({
  illustrationKind,
  statusCode,
  title,
  description,
  buttonLabel,
  testId,
}: ErrorPageProps) {
  const navigate = useNavigate();

  const handleBackToHome = () => {
    void navigate({ to: "/" });
  };

  return (
    <div
      className="flex flex-col items-center justify-center min-h-[calc(100vh-80px)] gap-6 px-6 text-center"
      data-testid={testId}
    >
      <Illustration kind={illustrationKind} />
      <Typography
        kind="display1-bold"
        color="--color-foreground-neutral-bold"
        ariaHidden="true"
      >
        {statusCode}
      </Typography>
      <Typography kind="h1" color="--color-foreground-neutral-bold">
        {title}
      </Typography>
      <Typography
        kind="body1"
        color="--color-foreground-neutral-medium"
        className="max-w-[480px]"
      >
        {description}
      </Typography>
      <Button kind="filled" size="large" onClick={handleBackToHome}>
        {buttonLabel}
      </Button>
    </div>
  );
}

export default ErrorPage;
